// Migration helper for legacy saved-search blobs.
//
// The Expanded Search Filters PRD (2026-05) added ~64 new fields to
// SearchFilters / CompanySearchFilters and revised the funding round
// enum from 14 to the verified 22-value set. Saved searches authored
// against the old shape (sitting in localStorage under
// ``cleanlist_saved_searches_<org>``) need to load cleanly without
// blowing up — and the user shouldn't have to redo their work.
//
// Strategy: on load, walk every saved search; pass its ``filters``
// blob through ``migrateSearchFilters`` / ``migrateCompanyFilters``.
// Each migrator is permissive — unknown fields pass through unchanged
// and missing new fields default to empty. Funding labels are
// rewritten to the canonical 22-value enum where the legacy label
// maps cleanly; ambiguous ones (e.g. "Series F+") map to the most
// specific match ("Series F").
//
// Type-guarded per field (round-2 reliability audit S2): a previously
// stored string ``"true"`` for a boolean field used to land in the
// boolean slot and silently turn the filter "active" on every render.
// Each field's expected runtime type is now validated before assignment;
// mismatches fall back to INITIAL_FILTERS' default for that field.

import {
  INITIAL_FILTERS,
  type CrmFilterSections,
  type SearchFilters,
} from '@/types/search-filters';
import {
  FUNDING_ROUND_TYPES,
  INITIAL_COMPANY_FILTERS,
  type CompanySearchFilters,
} from '@/utils/company-search.utils';
import type { SavedSearch } from '@/components/search/saved-searches.component';
import { isWellFormedCrmClause } from '@/utils/crm-section-clauses.util';

/**
 * Legacy → canonical funding round display labels. Used both for
 * People-side ``fundingRoundTypes`` array (if it ever existed; this
 * is forward-compat insurance) AND Company-side. Lowercase canonical
 * wire values pass through unchanged — only display labels mapped.
 */
const LEGACY_FUNDING_LABEL_MAP: Record<string, string> = {
  'Series F+': 'Series F',
  Venture: 'Series Unknown',
  Crowdfunding: 'Undisclosed',
  Debt: 'Debt Financing',
  // Already-canonical labels — no-op, listed for explicitness.
  'Pre-Seed': 'Pre-Seed',
  Seed: 'Seed',
  'Series A': 'Series A',
  'Series B': 'Series B',
  'Series C': 'Series C',
  'Series D': 'Series D',
  'Series E': 'Series E',
  Angel: 'Angel',
  Grant: 'Grant',
  'Private Equity': 'Private Equity',
};

// Validate against the canonical UI option list — anything not in
// FUNDING_ROUND_TYPES after legacy mapping is a label we don't know
// how to send to Crustdata, so we drop it rather than letting it
// reach buildCompanyFilters and silently disappear on lookup. The
// previous ``?? label`` fallback let arbitrary strings survive, which
// meant a typo'd or hand-edited saved-search blob could carry a
// totally bogus value all the way through. Round-3 reviewer catch.
const CANONICAL_FUNDING_LABELS = new Set<string>(FUNDING_ROUND_TYPES);

function migrateFundingLabel(label: unknown): string | null {
  if (typeof label !== 'string') return null;
  const mapped = LEGACY_FUNDING_LABEL_MAP[label] ?? label;
  return CANONICAL_FUNDING_LABELS.has(mapped) ? mapped : null;
}

// ─── Type guards (per-field migration validation) ───────────────────

/** Decide the runtime type of a default value so the migrator can
 *  validate incoming blobs against it without hardcoding per-field
 *  schemas. Returns one of the discriminator strings the validator
 *  knows about. */
type FieldKind = 'string' | 'boolean' | 'number' | 'array' | 'object';

function kindOf(value: unknown): FieldKind | 'unknown' {
  if (Array.isArray(value)) return 'array';
  if (value === null) return 'object'; // nullable objects (e.g. geoRadius)
  const t = typeof value;
  if (t === 'string' || t === 'boolean' || t === 'number' || t === 'object') {
    return t as FieldKind;
  }
  return 'unknown';
}

/** Return ``true`` iff ``incoming`` matches the runtime type of
 *  ``defaultValue``. Arrays are checked shallowly here; the migrators
 *  then keep only string elements (every list filter is ``string[]``
 *  except ``workforceRules``), because a chip renders each element and an
 *  object element crashed the whole page from a shared ``?aiFilters=`` /
 *  ``?q=`` link (React #31). Objects pass through identically to legacy
 *  behavior. */
function matchesKind(incoming: unknown, defaultValue: unknown): boolean {
  const want = kindOf(defaultValue);
  const got = kindOf(incoming);
  if (want === 'unknown' || got === 'unknown') return false;
  return want === got;
}

/** The canonical {salesforce, hubspot} section map holding only well-formed
 *  clauses (a nonblank id and condition, an include/exclude polarity). Always a
 *  fresh object, so no caller shares the defaults' arrays. */
function sanitizeCrmFilterSections(raw: unknown): CrmFilterSections {
  const clean: CrmFilterSections = { salesforce: [], hubspot: [] };
  if (!raw || typeof raw !== 'object') return clean;
  for (const crm of ['salesforce', 'hubspot'] as const) {
    const arr = (raw as Record<string, unknown>)[crm];
    if (!Array.isArray(arr)) continue;
    for (const c of arr) {
      if (isWellFormedCrmClause(c))
        clean[crm].push({
          id: c.id,
          polarity: c.polarity,
          condition: c.condition,
        });
    }
  }
  return clean;
}

const onlyStrings = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];

/**
 * Apply the legacy → current shape transform to a People-side filter
 * blob. Returns a *new* object that's safe to spread into setFilters().
 */
export function migrateSearchFilters(
  raw: Record<string, unknown>
): SearchFilters {
  const merged: SearchFilters = { ...INITIAL_FILTERS };
  // Per-key, type-validated copy. A field whose stored type doesn't
  // match the INITIAL_FILTERS default for that key is dropped silently
  // so the default sticks — preferable to corrupting downstream
  // filters with a wrong-type value.
  for (const key of Object.keys(INITIAL_FILTERS) as Array<
    keyof SearchFilters
  >) {
    const incoming = raw[key as string];
    if (incoming === undefined || incoming === null) continue;
    const defaultValue = INITIAL_FILTERS[key];
    if (!matchesKind(incoming, defaultValue)) continue;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (merged as any)[key] = Array.isArray(incoming)
      ? onlyStrings(incoming)
      : incoming;
  }

  // Legacy field fold-up: standalone Continent picker was retired
  // when the country grouped picker started exposing continents +
  // business regions (EMEA/APAC/…) as parent group tokens. Migrate
  // saved-search ``locationContinents`` values into ``contactCountries``
  // — continent names like "Europe" / "Asia" exactly match
  // COUNTRY_GROUPS entries so they re-render as group chips in the
  // unified picker. "Antarctica" has no member countries in our list,
  // so it silently drops.
  const legacyContinents = raw['locationContinents'];
  if (Array.isArray(legacyContinents) && legacyContinents.length > 0) {
    const existing = new Set(merged.contactCountries);
    for (const c of legacyContinents) {
      if (typeof c === 'string' && c !== 'Antarctica' && !existing.has(c)) {
        merged.contactCountries.push(c);
        existing.add(c);
      }
    }
  }

  // Coerce array shapes for any new field that arrived as null or
  // undefined in old blobs. Dormant fields removed in 2026-05
  // unification (excludeNames, neverHeldSeniority, workedAtAll*,
  // primaryEmploymentOnly, profileLanguage, honorsKeyword,
  // locationContinents) are silently dropped — old blobs that contain
  // them lose the unused keys, no error surfaced.
  const arrayKeys: Array<keyof SearchFilters> = [
    'currentCompanyLinkedInUrls',
    'currentCompanyDomains',
    'currentCompanyNames',
    'pastCompanyLinkedInUrls',
    'functionCategoriesInclude',
    'pastFunctionCategoriesInclude',
    'pastSeniority',
    'neverWorkedAtCompanies',
    'neverHeldTitles',
    'languages',
    'openToCards',
    'excludeProfileUrls',
    'excludeCompanyDomains',
    'excludeInCleanlistListIds',
    // Statuses this build doesn't know stay as saved: the search refuses
    // them by name rather than running without them.
    'excludeInInstantlyStatuses',
  ];
  for (const k of arrayKeys) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (!Array.isArray((merged as any)[k])) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (merged as any)[k] = [];
    }
  }

  // A wrong-typed In Instantly blocklist stays as saved, like the statuses:
  // the search refuses it by name rather than running with it off.
  const blocklist = raw['excludeInInstantlyBlocklist'];
  if (blocklist != null && typeof blocklist !== 'boolean') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (merged as any).excludeInInstantlyBlocklist = blocklist;
  }

  // geoRadius is nullable; pre-PRD blobs lack the key entirely. If
  // somehow loaded as a non-object or with bad shape, reset to null
  // so the picker / filter builder don't crash.
  const gr = merged.geoRadius as
    | { center?: unknown; radius?: unknown; unit?: unknown }
    | null
    | undefined;
  if (
    gr != null &&
    (typeof gr !== 'object' ||
      typeof gr.center !== 'string' ||
      typeof gr.radius !== 'number')
  ) {
    merged.geoRadius = null;
  }

  // CRM filter sections: matchesKind only verified this is an *object*, so a
  // corrupted / hand-edited blob could carry a clause missing
  // `condition`/`polarity`/`id`. getActiveCrmSectionFilters + buildCrmFilterChips
  // read `clause.condition.trim()`, so a malformed clause throws a TypeError
  // and crashes the whole search + chip strip — the opposite of "a pure-narrow
  // exclude never breaks the search". Coerce to the canonical
  // {salesforce:[], hubspot:[]} shape and keep ONLY well-formed clauses.
  merged.crmFilterSections = sanitizeCrmFilterSections(
    merged.crmFilterSections
  );

  // excludeInCleanlist: matchesKind only verified it's a *string*, so a junk
  // value ("banana") would survive and silently fall into the 'list'-scope
  // branch downstream. Clamp to the valid enum; anything else => 'off'.
  if (
    merged.excludeInCleanlist !== 'off' &&
    merged.excludeInCleanlist !== 'any' &&
    merged.excludeInCleanlist !== 'list'
  ) {
    merged.excludeInCleanlist = 'off';
  }

  return merged;
}

/**
 * Apply the legacy → current shape transform to a Company-side filter
 * blob. Same pass-through pattern as the people-side migrator, with
 * per-field runtime type guards.
 */
export function migrateCompanyFilters(
  raw: Record<string, unknown>
): CompanySearchFilters {
  const merged: CompanySearchFilters = { ...INITIAL_COMPANY_FILTERS };
  // Retain malformed restrictions too: dispatch validates and fails closed.
  if (raw.startupPrograms != null)
    merged.startupPrograms =
      raw.startupPrograms as CompanySearchFilters['startupPrograms'];
  for (const key of Object.keys(INITIAL_COMPANY_FILTERS) as Array<
    keyof CompanySearchFilters
  >) {
    const incoming = raw[key as string];
    if (incoming === undefined || incoming === null) continue;
    const defaultValue = INITIAL_COMPANY_FILTERS[key];
    if (!matchesKind(incoming, defaultValue)) continue;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (merged as any)[key] =
      Array.isArray(incoming) && key !== 'workforceRules'
        ? onlyStrings(incoming)
        : incoming;
  }
  merged.workforceRules = merged.workforceRules.filter(
    (rule) =>
      !!rule &&
      typeof rule === 'object' &&
      typeof rule.role === 'string' &&
      ['percent', 'growth_6m', 'growth_yoy'].includes(rule.metric) &&
      (rule.op === '>=' || rule.op === '<=') &&
      Number.isFinite(rule.threshold)
  );

  // Rewrite legacy funding round labels. Drops any that don't map.
  if (Array.isArray(merged.fundingRoundTypes)) {
    merged.fundingRoundTypes = merged.fundingRoundTypes
      .map((l) => migrateFundingLabel(l))
      .filter((l): l is string => !!l);
  }

  // Coerce arrays for new fields that may be missing in old blobs.
  const arrayKeys: Array<keyof CompanySearchFilters> = [
    'naicsCodes',
    'industriesExclude',
    'headcountRangesExclude',
    'companyTypeExclude',
    'hqCountriesIso3',
    'hqCountriesIso3Exclude',
    'largestHeadcountCountriesIso3',
    'hqStates',
    'hqStatesExclude',
    'hqCities',
    'hqCitiesExclude',
    'linkedinCategoriesInclude',
    'crunchbaseCategoriesInclude',
    'crunchbaseCategoriesExclude',
    'stockListings',
    'fundingRoundTypesExclude',
    'investorsInclude',
    'investorsExclude',
    'stockSymbols',
    'domainsList',
    'companyNamesList',
    'linkedinUrls',
    'workforceRules',
  ];
  for (const k of arrayKeys) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (!Array.isArray((merged as any)[k])) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (merged as any)[k] = [];
    }
  }

  // Company excludes: keep and sanitize them the way the people side does. A
  // stored inclusion survives so validation can refuse it by name; the arrays
  // are fresh so nothing aliases INITIAL_COMPANY_FILTERS.
  merged.excludeCompanyDomains = onlyStrings(merged.excludeCompanyDomains);
  merged.excludeInCleanlistListIds = onlyStrings(
    merged.excludeInCleanlistListIds
  );
  if (
    merged.excludeInCleanlist !== 'off' &&
    merged.excludeInCleanlist !== 'any' &&
    merged.excludeInCleanlist !== 'list'
  ) {
    merged.excludeInCleanlist = 'off';
  }
  merged.crmFilterSections = sanitizeCrmFilterSections(
    merged.crmFilterSections
  );

  return merged;
}

/**
 * Saved-search payload shape that supports the People-side composite
 * slice. Existing saved searches don't have this key — when absent the
 * loader falls back to ``emptyCompanyFilters()`` upstream.
 */
export const COMPOSITE_KEY = '__compositeCompanyFilters';

/** Extract the composite-company-filters slice that may be embedded
 *  inside a People-side SavedSearch.filters blob, or ``null`` if the
 *  blob predates composite routing (most existing saves). */
export function extractCompositeCompanyFilters(
  rawFilters: Record<string, unknown> | undefined | null
): CompanySearchFilters | null {
  if (!rawFilters || typeof rawFilters !== 'object') return null;
  const slice = rawFilters[COMPOSITE_KEY];
  if (!slice || typeof slice !== 'object' || Array.isArray(slice)) return null;
  return migrateCompanyFilters(slice as Record<string, unknown>);
}

/** Strip the composite slice from a filter blob before persisting the
 *  rest as a plain SearchFilters payload — keeps the on-disk shape
 *  symmetrical even though the runtime carries two slices. */
export function stripCompositeKey(
  filters: Record<string, unknown>
): Record<string, unknown> {
  if (!(COMPOSITE_KEY in filters)) return filters;
  const rest: Record<string, unknown> = {};
  for (const k of Object.keys(filters)) {
    if (k !== COMPOSITE_KEY) rest[k] = filters[k];
  }
  return rest;
}

/** Pack a people-side SavedSearch payload from its two state slices.
 *  Inverse of ``extractCompositeCompanyFilters``. The composite slice
 *  is stored under ``__compositeCompanyFilters`` so legacy consumers
 *  that only read named SearchFilters keys ignore it cleanly. */
export function packPeopleSavedSearch(
  filters: SearchFilters,
  compositeCompanyFilters: CompanySearchFilters
): Record<string, unknown> {
  return {
    ...(filters as unknown as Record<string, unknown>),
    [COMPOSITE_KEY]: compositeCompanyFilters as unknown as Record<
      string,
      unknown
    >,
  };
}

/**
 * Walk a SavedSearch array and re-stamp the ``filters`` blob through
 * the right migrator. Stable id + name preserved so the user's UI
 * pointers (list-of-saved-searches dropdown) keep working.
 */
export function migrateSavedSearches(searches: SavedSearch[]): SavedSearch[] {
  return searches.map((s) => {
    const filters = s.filters ?? {};
    if (s.searchType === 'company') {
      return {
        ...s,
        filters: migrateCompanyFilters(
          filters as Record<string, unknown>
        ) as unknown as Record<string, unknown>,
      };
    }
    // People-side: preserve the composite slice across migration. The
    // people filters migrator only sees the plain shape; composite
    // gets validated separately and re-packed at the end.
    const composite = extractCompositeCompanyFilters(
      filters as Record<string, unknown>
    );
    const plainMigrated = migrateSearchFilters(
      stripCompositeKey(filters as Record<string, unknown>)
    );
    if (composite) {
      return {
        ...s,
        filters: packPeopleSavedSearch(plainMigrated, composite),
      };
    }
    return {
      ...s,
      filters: plainMigrated as unknown as Record<string, unknown>,
    };
  });
}
